import { MIN_IMAGE_SIDE } from '@ai-photo-style/shared';
import { EmbedError } from './errors';

export interface PreparedPhoto {
  blob: Blob;
  width: number;
  height: number;
  /** Object-URL voor weergave; vrijgeven met URL.revokeObjectURL. */
  url: string;
}

const MAX_SIDE = 2048;
/** Foto's van telefoons zijn vaak 5–15 MB; we verkleinen ze toch, dus we laten ruim toe. */
const MAX_INPUT_BYTES = 40 * 1024 * 1024;

/**
 * Leest de gekozen foto in (met EXIF-rotatie), verkleint naar max. 2048 px en maakt er een
 * JPEG van. Zo gaat er ~1 MB over mobiel internet in plaats van de volledige camerafoto.
 */
export async function preparePhoto(file: File): Promise<PreparedPhoto> {
  const looksLikeImage = file.type.startsWith('image/') || /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
  if (!looksLikeImage) throw new EmbedError('IMAGE_UNSUPPORTED_TYPE');
  if (file.size > MAX_INPUT_BYTES) throw new EmbedError('IMAGE_TOO_LARGE');

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new EmbedError('IMAGE_UNSUPPORTED_TYPE');
  }
  try {
    if (Math.min(bitmap.width, bitmap.height) < MIN_IMAGE_SIDE) throw new EmbedError('IMAGE_TOO_SMALL');
    const scale = Math.min(1, MAX_SIDE / Math.max(bitmap.width, bitmap.height));
    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new EmbedError('INTERNAL');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(bitmap, 0, 0, width, height);
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new EmbedError('IMAGE_UNSUPPORTED_TYPE'))), 'image/jpeg', 0.9),
    );
    return { blob, width, height, url: URL.createObjectURL(blob) };
  } finally {
    bitmap.close();
  }
}

/**
 * KC's upload-endpoint weigert te grote bestanden (lokaal: nginx-standaard 1 MB, gezien als 413).
 * Maak het resultaat daarom kleiner dan `maxBytes`, met behoud van printresolutie (max 2048 px).
 * Een PNG (line art) blijft PNG, zodat de doorzichtige achtergrond niet op wit wordt gezet.
 * Een JPEG gaat eerst in kwaliteit omlaag, en pas daarna in afmetingen.
 */
export async function compressForUpload(
  blob: Blob,
  maxBytes = 900_000,
  maxSide = 2048,
): Promise<{ blob: Blob; width: number; height: number }> {
  const png = blob.type === 'image/png';
  const bitmap = await createImageBitmap(blob);
  try {
    let scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
    const qualities = png ? [undefined] : [0.9, 0.85, 0.8, 0.72];
    for (let round = 0; round < 4; round++) {
      const width = Math.round(bitmap.width * scale);
      const height = Math.round(bitmap.height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new EmbedError('INTERNAL');
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(bitmap, 0, 0, width, height);
      for (const quality of qualities) {
        const out = await new Promise<Blob>((resolve, reject) =>
          canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new EmbedError('INTERNAL'))),
            png ? 'image/png' : 'image/jpeg',
            quality,
          ),
        );
        if (out.size <= maxBytes) return { blob: out, width, height };
      }
      scale *= 0.85;
    }
    throw new EmbedError('IMAGE_TOO_LARGE');
  } finally {
    bitmap.close();
  }
}
